/* eslint-disable no-restricted-globals */
/* eslint-disable @typescript-eslint/no-explicit-any */
import axios from 'axios';
import Cookies from 'js-cookie';
import { TableCell, tableCellClasses, TableRow } from '@mui/material';
import { styled } from '@mui/system';
import { parseISO, format } from 'date-fns';
import { NavigateFunction } from 'react-router';
import { Authentication } from '../context/AuthContext';
import { getServices } from '../config/config';

const LOGIN_COOKIES: string[] = [
  'access_token',
  'refresh_token',
  'account_id',
  'is_organisation_admin',
  'license_types',
  'organisation',
  'organisation_has_licenses',
  'email_is_verified',
  'active_org_session',
  'error',
];

export const PASSWORD_POLICY_MESSAGE = 'Include minimum 8 characters, one uppercase letter, lowercase letter, number and a special character @$!%*?&';

export const isValidPassword = (password: string) => /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/.test(password);

const removeCookieEverywhere = (cookieName: string) => {
  Cookies.remove(cookieName);
  Cookies.remove(cookieName, { path: '/' });
  Cookies.remove(cookieName, { path: '/', domain: window.location.hostname });
  Cookies.remove(cookieName, { path: '/', domain: `.${window.location.hostname}` });
};

export const clearLoginCookies = () => {
  LOGIN_COOKIES.forEach((cookieName) => {
    removeCookieEverywhere(cookieName);
  });
};

// Helper function to clear ALL cookies from the domain (nuclear option)
export const clearAllCookies = () => {
  // Get all cookie names using js-cookie
  const allCookies = Cookies.get();
  Object.keys(allCookies).forEach((cookieName) => {
    removeCookieEverywhere(cookieName);
  });
};

export function emailIsValid(email: string) {
  return /\S+@\S+\.\S+/.test(email);
}

export function isNumeric(num: any) {
  return !isNaN(num);
}

export function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

export function dateConverter(ISODate: any) {
  const date = new Date(ISODate);
  const year = date.getFullYear();
  const monthNumber = date.getMonth() + 1;
  let monthString;
  const dt = date.getDate();

  switch (monthNumber) {
    case 1:
      monthString = 'January';
      break;
    case 2:
      monthString = 'February';
      break;
    case 3:
      monthString = 'March';
      break;
    case 4:
      monthString = 'April';
      break;
    case 5:
      monthString = 'May';
      break;
    case 6:
      monthString = 'June';
      break;
    case 7:
      monthString = 'July';
      break;
    case 8:
      monthString = 'August';
      break;
    case 9:
      monthString = 'September';
      break;
    case 10:
      monthString = 'October';
      break;
    case 11:
      monthString = 'November';
      break;
    case 12:
      monthString = 'December';
      break;
    default:
      monthString = 'Invalid month';
  }

  return `${dt} ${monthString} ${year}`;
}

export function ISOToAusDate(ISODate: any) {
  const ISOToLongFormat = parseISO(ISODate.substring(0, 18));
  const response = format(new Date(ISOToLongFormat), 'MM/dd/yyyy');
  return response;
}

export function ISOToJSDate(ISODate: any) {
  const ISOToLongFormat = ISODate;
  const response = new Date(ISOToLongFormat);
  return response;
}

export function DateToISO(date: any) {
  const response = new Date(date).toISOString();
  return response;
}

// ***** AXIOS REQUEST TO GET/POST DATA ***** //
const apiPrefix = getServices().serviceURL;

export const getAccountId = () => {
  const accountId = Cookies.get('account_id');
  return { accountId };
};
export const getOrgId = () => {
  const selectedOrgFromCookie = Cookies.get('selectedOrganisation');
  const orgId = Cookies.get('organisation');

  const orgIdToUse = selectedOrgFromCookie || orgId;
  return { orgId: orgIdToUse };
};
const getAuthToken = () => {
  const accessToken = Cookies.get('access_token') ?? '';
  return { accessToken };
};

const getRefreshToken = () => {
  const refreshToken = Cookies.get('refresh_token');
  return { refreshToken };
};

const AUD_TOKEN_REGEX = /\bAUD\b/gi;

const normalizeCurrencyCode = (currency: string) => {
  if (typeof currency !== 'string') return '';
  const trimmed = currency.trim();
  return trimmed.length > 0 ? trimmed : '';
};

export const getCurrentOrgCurrency = () => {
  const selectedCurrency = Cookies.get('selectedOrganisationCurrency');
  const fallbackCurrency = Cookies.get('organisationCurrency');
  return selectedCurrency || fallbackCurrency || 'AUD';
};

export const replaceCurrencyToken = (value: string, currency: string) => {
  const normalizedCurrency = normalizeCurrencyCode(currency);
  if (typeof value !== 'string' || !normalizedCurrency) return value;
  return value.replace(AUD_TOKEN_REGEX, normalizedCurrency);
};

export const replaceTemplateCurrency = (
  templates: any[],
  currency: string,
) => {
  if (!Array.isArray(templates)) return templates;
  const normalizedCurrency = normalizeCurrencyCode(currency);
  if (!normalizedCurrency) return templates;

  return templates.map((template) => {
    if (!template || typeof template !== 'object') return template;

    const updatedTemplate: any = { ...template };

    if (typeof updatedTemplate.name === 'string') {
      updatedTemplate.name = replaceCurrencyToken(updatedTemplate.name, normalizedCurrency);
    }

    if (Array.isArray(updatedTemplate.structure)) {
      updatedTemplate.structure = updatedTemplate.structure.map((item: any) => {
        if (!item || typeof item !== 'object') return item;
        const updatedItem: any = { ...item };
        if (typeof updatedItem.unit === 'string') {
          updatedItem.unit = replaceCurrencyToken(updatedItem.unit, normalizedCurrency);
        }
        if (typeof updatedItem.display_unit === 'string') {
          updatedItem.display_unit = replaceCurrencyToken(
            updatedItem.display_unit,
            normalizedCurrency,
          );
        }
        return updatedItem;
      });
    }

    return updatedTemplate;
  });
};

export const generateBearerHeader = () => {
  const { accessToken } = getAuthToken();
  return {
    ...(accessToken
      ? {
        headers: { Authorization: `Bearer ${accessToken}` },
      }
      : {}),
  };
};

export const getData = async (
  endpoint: string,
  authorizationHeader?: { headers: { Authorization: string } },
) => {
  try {
    const headers = authorizationHeader?.headers?.Authorization
      ? authorizationHeader
      : generateBearerHeader();

    const response = await axios.get(`${apiPrefix}${endpoint}`, headers);
    return response;
  } catch (error: any) {
    if (error.response?.status === 401) {
      // Handle unauthorized access, e.g., redirect to login
      clearAllCookies();
      window.location.href = '/';
      return error.response.data;
    }
    return error.response;
  }
};

export const postData = async (
  endpoint: string,
  body: object,
  noAuth?: boolean,
) => {
  try {
    let response = {};
    if (noAuth) {
      response = await axios.post(`${apiPrefix}${endpoint}`, body);
    } else {
      response = await axios.post(
        `${apiPrefix}${endpoint}`,
        body,
        generateBearerHeader(),
      );
    }
    return response;
  } catch (error: any) {
    if (error.response?.status === 401 && error.response?.statusText === 'Unauthorized') {
      // Handle unauthorized access, e.g., redirect to login
      clearAllCookies();
      window.location.href = '/';
      return error.response.data;
    }
    return error.response;
  }
};

export const patchData = async (endpoint: string, body: object) => {
  try {
    const response = await axios.patch(
      `${apiPrefix}${endpoint}`,
      body,
      generateBearerHeader(),
    );

    return response;
  } catch (error: any) {
    if (error.response?.status === 401 && error.response?.statusText === 'Unauthorized') {
      // Handle unauthorized access, e.g., redirect to login
      clearAllCookies();
      window.location.href = '/';
      return error.response.data;
    }
    return error.response;
  }
};

export const deleteData = async (endpoint: string) => {
  try {
    const response = await axios.delete(
      `${apiPrefix}${endpoint}`,
      generateBearerHeader(),
    );
    return response;
  } catch (error: any) {
    if (error.response?.status === 401 && error.response?.statusText === 'Unauthorized') {
      // Handle unauthorized access, e.g., redirect to login
      clearAllCookies();
      window.location.href = '/';
      return error.response.data;
    }
    return error.response;
  }
};
// ***** END OF AXIOS REQUEST TO GET/POST DATA ***** //

// GENERATE RANDOM COLOR
export const randomRGB = () => {
  const min = 0;
  const max = 255;
  const randomR = Math.floor(Math.random() * (max - min + 1) + min);
  const randomG = Math.floor(Math.random() * (max - min + 1) + min);
  const randomB = Math.floor(Math.random() * (max - min + 1) + min);
  return `${randomR}, ${randomG}, ${randomB}`;
};
// END OF GENERATE RANDOM COLOR

// START OF GET USER
export const getCurrentUser = async (navigate: NavigateFunction) => {
  const { accountId } = getAccountId();
  const { accessToken } = getAuthToken();
  const { refreshToken } = getRefreshToken();
  if (accountId) {
    const getCurrentUserResponse = await getData(`/accounts/${accountId}`);

    if (getCurrentUserResponse.status === 200) {
      // If could get user
      return getCurrentUserResponse;
    }
  }
  // IF could NOT get user
  const body = {
    id: accountId,
    access_token: accessToken,
    refresh_token: refreshToken,
  };
  const getNewTokens = await postData('/accounts/refreshtokens', body);
  if (getNewTokens.status === 200) {
    // If tokens refreshed
    Cookies.set('access_token', getNewTokens.data.access_token, {
      sameSite: 'strict',
    });
    Cookies.set('refresh_token', getNewTokens.data.refresh_token, {
      sameSite: 'strict',
    });
    // alert("Your session has expired. We will try to auto-login.");
    const getCurrentUserSecondResponse = await getData(
      `/accounts/${accountId}`,
    );
    return getCurrentUserSecondResponse;
  }
  Cookies.remove('access_token');
  Cookies.remove('refresh_token');
  Cookies.remove('account_id');
  navigate('/');
  navigate(0); // for refresh
  return '';
};
// END OF GET USER

// START OF GET ORG
export const getCurrentOrg = async (navigate: NavigateFunction) => {
  const currentUser = await getCurrentUser(navigate);
  if (currentUser.status === 404) {
    Cookies.remove('access_token');
    Cookies.remove('refresh_token');
    Cookies.remove('account_id');
  }

  const getCurrentOrgResponse = await getData(
    `/organisations/${currentUser.data.active_org_session}`,
  );

  return getCurrentOrgResponse;
};
// END OF GET ORG

export const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: '#61b9c3',
    color: 'white',
    textAlign: 'center',
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    textAlign: 'center',
  },
}));

export const StyledTableRow = styled(TableRow)(() => ({
  '&:nth-of-type(odd)': {
    backgroundColor: 'rgb(234, 234, 234)',
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

export const getBase64 = (file: any) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = function onload() {
    resolve(reader.result);
  };
  reader.onerror = reject;
  reader.readAsDataURL(file);
});
// End of GET BASE64

// Download a file when source is base64
export const downloadfile = (
  fileTypeArg: string,
  linkSourceArg: string,
  fileNameArg: string,
) => {
  // Eg. fileTypeArg = 'data:application/pdf;base64'
  // Eg. linkSourceArg = BASE64_STRING
  // Eg. fileNameARg = example.pdf

  const linkSource = `${fileTypeArg},${linkSourceArg}`;
  const downloadLink = document.createElement('a');
  const fileName = `${fileNameArg}`;

  downloadLink.href = linkSource;
  downloadLink.download = fileName;
  downloadLink.click();
};
// End of Download a file when source is base64

export const prettyModelName = (modelName: string) => modelName.charAt(0).toUpperCase() + modelName.slice(1).replaceAll('_', ' ');

export const downloadURI = (data: string, name: string) => {
  const link: any = document.createElement('a');
  link.download = name;
  link.href = data;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const setActiveOrgSession = async (locationId: string, currentUser: any) => {
  if (locationId) {
    try {
      await patchData(
        `/entities/${locationId}/org-switch?organisation_id=${currentUser.organisation}`,
        { account_id: currentUser.id, change_active_session: true },
      );
      return true;
    } catch (err) {
      return false;
    }
  }
  return true;
};

export const returnBackToLogin = async () => {
  try {
    const selectedOrgId = Cookies.get('selectedOrganisation');
    const { accountId } = getAccountId();
    if (selectedOrgId && Cookies.get('organisation')) {
      await setActiveOrgSession(selectedOrgId, {
        id: accountId,
        organisation: Cookies.get('organisation'),
      });
    }
  } catch (error) {
    clearAllCookies();
    window.location.href = '/'; // or your intended logout target
    return;
  }
  clearAllCookies();
  // Force hard redirect to login page (kills React state, avoids race conditions)
  window.location.href = '/'; // or your intended logout target
};

export const getAuthTokensFromBrowser = (): Authentication => {
  const authObject: any = {};

  LOGIN_COOKIES.forEach((fieldName) => {
    let value = Cookies.get(fieldName) ?? '';
    // check if JSON
    if (
      ['{', '['].includes(value?.trim()[0])
      || ['true', 'false'].includes(value)
    ) {
      value = JSON.parse(value);
    }
    authObject[fieldName] = value;
  });
  return authObject as Authentication;
};

export const storeAuthTokensToBrowser = (tokens: Authentication) => {
  clearLoginCookies();

  Object.keys(tokens).forEach((fieldName) => {
    let value: any = tokens[fieldName as keyof Authentication];
    if (typeof value === 'string' || value instanceof String) {
      Cookies.set(fieldName, value.trim());
    } else {
      value = JSON.stringify(value);
      Cookies.set(fieldName, value);
    }
  });
};

export const capitalizeFirstLetter = (value: string) => {
  const firstLetter = value.charAt(0).toUpperCase();
  const remainingLetters = value.slice(1);
  return firstLetter + remainingLetters;
};

export const formatDisplayName = (input: string) => input
  .toLowerCase()
  .split(' ')
  .map((word, index) => (index < 2 && word.length > 0
    ? word[0].toUpperCase() + word.slice(1)
    : word))
  .join(' ');
